import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL || "";
export interface ReflectorBgpConfig {
    localAs: number;
    routerId: string;
    routerId2?: string;
}

export interface ReflectorBgpNeighbor {
    neighborAddress: string;
    interfaceAddressId: number;
    peerAs: number;
    localAs: number;
    description: string;
    operatingMode: string;
    holdTime: number;
    keepAliveInterval: number;
    connectRetry: number;
    gracefulRestart: boolean;
    addPath: boolean;
    BFD: boolean;
    bfdInterval: number;
    bfdMultiplier: number;
    password?: string | null;
}

export interface CtxrConfig {
    reflectorInstanceId: number;
    interfaceAddressId: number;
}

export interface BgpNeighbor {
    base_config: ReflectorBgpNeighbor;
    ctxr_config: CtxrConfig | CtxrConfig[];
}

export interface ReflectorLicense {
    tenantId: number;
    subscription?: string;
    description: string;
    type: string;
    startDate: string;
    endDate: string;
    numberOfDevices: number;
}

/**
 * Interface for VRF configuration in Telco mode
 */
// Interface for VRF form data (from UI)
export interface VRFFormData {
  name: string;
  routeTargets: string[];
  newRouteTarget: string;
  routingTable: string;
  nextHopEni: string;
  policySelection: string;
  nextHopSelection: string;
  selectedRoutingTable?: string;
  selectedEni?: string;
  routingTableTagName?: string;
  routingTableTagValue?: string;
  nextHopTagName?: string;
  nextHopTagValue?: string;
  routingTables: string[];
  enis: string[];
  expanded: boolean;
  selectedVpc?: string; // Add VPC selection for Telco mode
  inboundId?: number | null; // Add inboundId for Telco mode updates
}

export interface VRFConfig {
  vrfName: string;
  routeTargets: string;
  routingTableType: "specific" | "dynamic";
  routingTables?: string[];
  routingTagName?: string;
  routingTagValue?: string;
  nextHopType: "ENI" | "TAG";
  eni?: string; // Changed from enis to eni for Telco mode
  nextHopTagName?: string;
  nextHopTagValue?: string;
  mode: "TELCO" | "ENTERPRISE";
  vpcId?: string; // Add vpcId field for each VRF
  inboundId?: number | null; // Add inboundId for Telco mode updates
}

/**
 * Interface for inbound propagation rule payload
 */
export interface InboundPropagationRulePayload {
  bgpNeighborId: number;
  mode?: "TELCO" | "ENTERPRISE"; // Optional for Telco mode with multiple VRFs
  // Single VRF fields (for backward compatibility)
  vrfName?: string; // Only for TELCO mode
  routeTargets?: string; // For both TELCO and ENTERPRISE modes
  // Multiple VRF fields (for Telco mode with multiple VRFs)
  vrfs?: VRFConfig[]; // Only for TELCO mode with multiple VRFs
  // Common fields (optional for Telco mode with multiple VRFs)
  routingTableType?: "specific" | "dynamic";
  routingTables?: string[]; // Only when routingTableType is "specific"
  routingTagName?: string; // Only when routingTableType is "dynamic"
  routingTagValue?: string; // Only when routingTableType is "dynamic"
  nextHopType?: "ENI" | "TAG";
  eni?: string; // Only when nextHopType is "ENI"
  nextHopTagName?: string; // Only when nextHopType is "TAG"
  nextHopTagValue?: string; // Only when nextHopType is "TAG"
  vpcId?: string; // VPC selection for both Enterprise and Telco modes
}

/**
 * Interface for outbound propagation rule payload
 */
export interface OutboundPropagationRulePayload {
  bgpNeighborId: number;
  mode: "TELCO" | "ENTERPRISE";
  bgpCommunity?: string; // Optional for both modes
  nexthopIPAddress?: string; // Optional for both modes
  subnets: string[]; // Array of subnet CIDR blocks
}

/**
 * Helper function to build inbound propagation rule payload based on form values
 */
export function buildInboundPropagationRulePayload(
  bgpNeighborId: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: any,
  isTelco = false
): InboundPropagationRulePayload {
  // Add TELCO-specific fields
  if (isTelco) {
    if (values.vrfs && values.vrfs.length > 0) {
      // Handle multiple VRFs
      if (values.vrfs.length > 1) {
        // Multiple VRFs - create array of VRF configurations
        const vrfConfigs: VRFConfig[] = values.vrfs.map((vrf: VRFFormData) => {
          const vrfConfig: VRFConfig = {
            vrfName: vrf.name,
            routeTargets: vrf.routeTargets.join(", "),
            routingTableType: vrf.policySelection === "specific" ? "specific" : "dynamic",
            nextHopType: vrf.nextHopSelection === "specific" ? "ENI" : "TAG",
            mode: isTelco ? "TELCO" : "ENTERPRISE",
            vpcId: vrf.selectedVpc, // Add vpcId inside each VRF
            inboundId: vrf.inboundId || null, // Add inboundId for updates
          };
          
          // Handle routing table configuration
          if (vrf.policySelection === "specific") {
            vrfConfig.routingTables = vrf.routingTables || [];
          } else {
            vrfConfig.routingTagName = vrf.routingTableTagName;
            vrfConfig.routingTagValue = vrf.routingTableTagValue;
          }
          
          // Handle next-hop configuration
          if (vrf.nextHopSelection === "specific") {
            // For Telco mode, send only the first ENI as a string
            vrfConfig.eni = vrf.enis && vrf.enis.length > 0 ? vrf.enis[0] : undefined;
          } else {
            vrfConfig.nextHopTagName = vrf.nextHopTagName;
            vrfConfig.nextHopTagValue = vrf.nextHopTagValue;
          }
          
          return vrfConfig;
        });
        
        return {
          bgpNeighborId,
          mode: "TELCO",
          vrfs: vrfConfigs,
        };
      } else {
        // Single VRF - always use vrfs array format for Telco mode
        const vrf: VRFFormData = values.vrfs[0];
        
        const vrfConfig: VRFConfig = {
          vrfName: vrf.name,
          routeTargets: vrf.routeTargets.join(", "),
          routingTableType: vrf.policySelection === "specific" ? "specific" : "dynamic",
          nextHopType: vrf.nextHopSelection === "specific" ? "ENI" : "TAG",
          mode: "TELCO",
          vpcId: vrf.selectedVpc, // Add vpcId inside each VRF
          inboundId: vrf.inboundId || null, // Add inboundId for updates
        };
        
        // Handle routing table configuration
        if (vrf.policySelection === "specific") {
          vrfConfig.routingTables = vrf.routingTables || [];
        } else {
          vrfConfig.routingTagName = vrf.routingTableTagName;
          vrfConfig.routingTagValue = vrf.routingTableTagValue;
        }
        
        // Handle next-hop configuration
        if (vrf.nextHopSelection === "specific") {
          // For Telco mode, send only the first ENI as a string
          vrfConfig.eni = vrf.enis && vrf.enis.length > 0 ? vrf.enis[0] : undefined;
        } else {
          vrfConfig.nextHopTagName = vrf.nextHopTagName;
          vrfConfig.nextHopTagValue = vrf.nextHopTagValue;
        }
        
        return {
          bgpNeighborId,
          mode: "TELCO",
          vrfs: [vrfConfig],
        };
      }
    }
  }
  
  // Enterprise mode or fallback
  const basePayload: InboundPropagationRulePayload = {
    bgpNeighborId,
    mode: "ENTERPRISE",
    routingTableType: values.policySelection === "specific" ? "specific" : "dynamic",
    nextHopType: values.nextHopSelection === "specific" ? "ENI" : "TAG",
  };

  // Enterprise mode - add routeTargets if available (for consistency with Telco mode)
  if (values.routeTargets && values.routeTargets.length > 0) {
    basePayload.routeTargets = values.routeTargets.join(", ");
  }

  // Enterprise mode
  if (values.policySelection === "specific") {
    basePayload.routingTables = values.routingTables || [];
  } else {
    basePayload.routingTagName = values.routingTableTagName;
    basePayload.routingTagValue = values.routingTableTagValue;
  }
  
  // Add VPC selection for Enterprise mode
  if (values.selectedVpc) {
    basePayload.vpcId = values.selectedVpc;
  }
  
  if (values.nextHopSelection === "specific") {
    // For Enterprise mode, send only the first ENI as a string
    basePayload.eni = values.enis && values.enis.length > 0 ? values.enis[0] : undefined;
  } else {
    basePayload.nextHopTagName = values.nextHopTagName;
    basePayload.nextHopTagValue = values.nextHopTagValue;
  }

  return basePayload;
}

/**
 * Helper function to build outbound propagation rule payload based on form values
 */
export function buildOutboundPropagationRulePayload(
  bgpNeighborId: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: any,
  isTelco = false
): OutboundPropagationRulePayload {
  const basePayload: OutboundPropagationRulePayload = {
    bgpNeighborId,
    mode: isTelco ? "TELCO" : "ENTERPRISE",
    subnets: values.subnets || [],
  };

  // Add optional fields if they exist (same for both modes)
  if (values.bgpCommunity) {
    basePayload.bgpCommunity = values.bgpCommunity;
  }
  if (values.nexthopIPAddress) {
    basePayload.nexthopIPAddress = values.nexthopIPAddress;
  }

  return basePayload;
}

/*** 
 * GET /instances
 * Lists all ReflectorInstances with cluster information.
 */
export function getReflectorsList(token: string, tenantId: number) {
    return axios.get(`${API_URL}/v2/reflector/instances?tenantId=${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * GET /instances/{id}/overview
  Gets complete details of a specific ReflectorInstance.
 */
export function getReflectorDetails(token: string, reflectorId: number) {
    return axios.get(`${API_URL}/v2/reflector/instances/${reflectorId}/overview`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

//BGP CONFIG

/**
 * GET /instances/:id/bgp-config
 * Gets BGP configuration of a specific ReflectorInstance.
 */
export function getReflectorBgpConfig(token: string, reflectorId: number) {
    return axios.get(`${API_URL}/v2/reflector/instances/${reflectorId}/bgp-config`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/** PUT /instances/:id/bgp-config
    Updates the global BGP configuration and automatically generates hash.
 */
export function updateReflectorBgpConfig(token: string, reflectorId: number, payload: ReflectorBgpConfig) {
    return axios.put(`${API_URL}/v2/reflector/instances/${reflectorId}/bgp-config`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * POST /instances/:id/bgp-neighbors
 *  Creates a new BGP neighbor and automatically generates hash.
 */
export function createReflectorBgpNeighbor(token: string, reflectorId: number, payload: ReflectorBgpNeighbor) {
    return axios.post(`${API_URL}/v2/reflector/instances/${reflectorId}/bgp-neighbors`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/** POST v2/reflector/bgp-neighbors/:id
 *  Creates a new BGP Neighbor on id we can send cluster or reflector ID
 */
export function createBgpNeighbor(token: string, Id: number, payload: BgpNeighbor) {
    return axios.post(`${API_URL}/v2/reflector/bgp-neighbors/${Id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**PUT v2/reflector/bgp-neighbors/:Id
 * Update BGP Neighbor on id we can send cluster or reflector ID
 */
export function updateBgpNeighbor(token: string, Id: number, payload: BgpNeighbor) {
    return axios.put(`${API_URL}/v2/reflector/bgp-neighbors/${Id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * GET /instances/:id/bgp-neighbors
    Gets list of BGP neighbors with their associated prefixes.
 */
export function getReflectorBgpNeighbors(token: string, reflectorId: number) {
    return axios.get(`${API_URL}/v2/reflector/instances/${reflectorId}/bgp-neighbors`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}
/** DELETE /instances/:id/bgp-neighbors/:neighborId
    Deletes a BGP neighbor.
 */
export function deleteReflectorBgpNeighbor(token: string, neighborId: number) {
    return axios.delete(`${API_URL}/v2/reflector/bgp-neighbor/${neighborId}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * GET /instances/:id/interfaces
 * Gets list of interfaces for a specific ReflectorInstance.
 */
export function getReflectorInterfaces(token: string, reflectorId: number) {
    return axios.get(`${API_URL}/v2/reflector/instances/${reflectorId}/interfaces`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**PUT /v2/reflector/instances/:id/name
 * Updates the name of a specific ReflectorInstance.
 */
export function updateReflectorName(token: string, reflectorId: number, payload: { name: string }) {
    return axios.put(`${API_URL}/v2/reflector/instances/${reflectorId}/name`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**PUT v2/reflector/instances/:id/tags
 * Updates the tags of a specific ReflectorInstance.
 */
export function updateReflectorTags(token: string, reflectorId: number, payload: { tags: string }) {
    return axios.put(`${API_URL}/v2/reflector/instances/${reflectorId}/tags`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}



//CLOUD PROPAGATION

/**
 * GET /instances/:id/cloud-propagation-rules
 * Gets cloud propagation rules for the instance.
 */
export function getCloudPropagationRules(token: string, reflectorId: number) {
    return axios.get(`${API_URL}/v2/reflector/instances/${reflectorId}/propagation-rules/grouped`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * GET /v2/reflector/clusters/:clusterId/propagation-rules
 * Gets cloud propagation rules for cluster
 */
export function getCloudPropagationRulesByCluster(token: string, clusterId:number){
    return axios.get(`${API_URL}/v2/reflector/clusters/${clusterId}/propagation-rules/grouped`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/** GET v2/reflector/bgp-neighbors/:bgpNeighborId/propagation-rules
 * Gets cloud propagation rule details for a specific BGP neighbor.
 */
export function getCloudPropagationRuleDetails(token: string, bgpNeighborId:number){
    return axios.get(`${API_URL}/v2/reflector/bgp-neighbors/${bgpNeighborId}/propagation-rules`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}
/**
 * CREATE INBOUND Propagation Rule Reflector Standalone
 * POST /v2/reflector/bgp-neighbors/:bgpNeighborId/propagation-rules/inbound
 */
export function createInboundPropagationRule(token: string, bgpNeighborId: number, payload: InboundPropagationRulePayload) {
    return axios.post(`${API_URL}/v2/reflector/bgp-neighbors/${bgpNeighborId}/propagation-rules/inbound`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * CREATE OUTBOUND Propagation Rule Reflector Standalone
 * POST v2/reflector/bgp-neighbors/:bgpNeighborId/propagation-rules/outbound
 */
export function createOutboundPropagationRule(token: string, bgpNeighborId: number, payload: OutboundPropagationRulePayload) {
    return axios.post(`${API_URL}/v2/reflector/bgp-neighbors/${bgpNeighborId}/propagation-rules/outbound`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * PUT v2/reflector/bgp-neighbors/:bgpneighborId/propagation-rules/inbound/:inboundId
 * Update inbound propagation rule by neighborId and inboundId
 */
export function updateInboundPropagationRule(token: string, neighborId: number, inboundId: number, payload: InboundPropagationRulePayload) {
    return axios.put(`${API_URL}/v2/reflector/bgp-neighbors/${neighborId}/propagation-rules/inbound/${inboundId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}
/**
 * PUT /v2/reflector/bgp-neighbors/:neighborId/propagation-rules/outbound/:outboundId 
 * Update outbound propagation rule
 */
export function updateOutboundPropagationRule(token: string, neighborId: number, outboundId: number, payload: OutboundPropagationRulePayload) {
    return axios.put(`${API_URL}/v2/reflector/bgp-neighbors/${neighborId}/propagation-rules/outbound/${outboundId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/** GET v2/reflector/cloudelements/instances/:reflectorId
 * Gets route tables and network interfaces for a reflector
 */
export function getRouteTablesAndNetworkInterfaces(token: string, reflectorId: number) {
    return axios.get(`${API_URL}/v2/reflector/cloudelements/instances/${reflectorId}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**GET v2/reflector/cloudelements/clusters/:clusterId 
 * Gets route tables and network interfaces for a cluster
 */
export function getRouteTablesAndNetworkInterfacesForCluster(token: string, clusterId: number) {
    return axios.get(`${API_URL}/v2/reflector/cloudelements/clusters/${clusterId}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/** GET v2/reflector/cloudelements/instances/:reflectorId/subnets
 * Gets subnets for a reflector
 */
export function getSubnetsForReflector(token: string, reflectorId: number) {
    return axios.get(`${API_URL}/v2/reflector/cloudelements/instances/${reflectorId}/subnets`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}
/** GET /v2/reflector/cloudelements/clusters/:clusterId/subnets
 * Gets subnets for a cluster
 */
export function getSubnetsForCluster(token: string, clusterId: number) {
    return axios.get(`${API_URL}/v2/reflector/cloudelements/clusters/${clusterId}/subnets`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}




//BGP NEIGHBORS
/**
 * GET /clusters/:id/bgp-neighbors
 * Gets list of BGP neighbors for a specific cluster.
 */
export function getClusterBgpNeighbors(token: string, clusterId: number) {
    return axios.get(`${API_URL}/v2/reflector/clusters/${clusterId}/bgp-neighbors/grouped`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}



//IAM PERMISSIONS

/**
 * GET /instances/:id/iam-permissions
 * Gets IAM permissions for a specific ReflectorInstance.
 */
export function getReflectorIamPermissions(token: string, reflectorId: number) {
    return axios.get(`${API_URL}/v2/reflector/instances/${reflectorId}/iam-permissions`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

//LICENSING

/**POST /reflector/generate-token 
 * Generates a token for a specific reflector license.
*/
export function generateToken(token: string) {
    return axios.post(`${API_URL}/v2/reflector/generate-token`, null, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**POST /reflector/regenerate-token 
 * Regenerates a token for a specific reflector license.
*/
export function regenerateToken(token: string, payload: { token: string }) {
    return axios.post(`${API_URL}/v2/reflector/generate-token`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**GET /reflector/license/list 
 * Lists all reflector licenses.
*/
export function getReflectorLicenses(token: string) {
    return axios.get(`${API_URL}/v2/reflector/license/list`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**PUT /reflector/license/:id 
 * Updates a reflector license.
*/
export function updateReflectorLicense(token: string, licenseId: number, payload: { description: string }) {
    return axios.put(`${API_URL}/v2/reflector/license/${licenseId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**POST /reflector/license 
 * Creates a new reflector license.
*/
export function createReflectorLicense(token: string, payload: ReflectorLicense) {
    return axios.post(`${API_URL}/v2/reflector/license`, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * GET /v1/tenant/customers?tenantId=xxx
 * Fetch customers for a specific tenant
 */
export function fetchCustomersWithTenant(token: string, tenantId: number) {
    return axios.get(`${API_URL}/v1/tenant/customers?tenantId=${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * GET /v1/tenant/partners?tenantId=xxx
 * Fetch partners for a specific tenant
 */
export function fetchPartnersWithTenant(token: string, tenantId: number) {
    return axios.get(`${API_URL}/v1/tenant/partners?tenantId=${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}





